import { useQuery } from '@tanstack/react-query';
import { TFunction } from 'i18next/typescript/t';
import { useMemo } from 'react';

import { OrthoTileModel } from '../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../utils/variables';
import { ProjectGeodataManager } from '../../viewer/project-geodata-manager';
import { useProjectSnapshot } from './use-project-snapshot';

export type TimelineOrthotileInfo = {
  id: string;
  name: string;
  index: number;
  orthotileId: string;
  geodataId?: string;
};

export function useProjectOrthotiles() {
  const { projectId: rawProjectId } = useProjectSnapshot();
  const projectId = useMemo(() => rawProjectId ?? undefined, [rawProjectId]);

  const { data } = useQuery({
    queryKey: ['getProjectOrthotiles', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      if (!isDefined(projectId)) {
        return [];
      }
      const response = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV0()
        .getOrthoTiles({
          path: { projectId },
        });

      return response.data;
    },
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
  });

  return data;
}

export function getTimelineOrthotileInfo(
  orthotile: OrthoTileModel,
  t: TFunction,
): TimelineOrthotileInfo | undefined {
  const timelineContainer = ProjectGeodataManager.instance()
    .getTimelineContainers()
    .find((x) => x.orthotile === orthotile.id);

  if (!timelineContainer) {
    return;
  }

  const activeTimeline = ProjectGeodataManager.instance().getActiveTimelineContainer();
  let displayName = `${t('terrainModel.timeline', { ns: 'skyviewAssetMenu' })} ${timelineContainer.containerIndex.toString()} (${orthotile.fileName} ${
    timelineContainer.displayDate
  })`;

  if (activeTimeline && timelineContainer.id === activeTimeline.id) {
    displayName = displayName + ` (${t('timeline.current', { ns: 'skyview' })})`;
  }

  return {
    id: timelineContainer.containerIndex.toString(),
    index: timelineContainer.containerIndex,
    orthotileId: orthotile.id,
    geodataId: orthotile.parentGeodataId,
    name: displayName,
  };
}
